import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';

import {
	getBodyOfSlideSequence,
	getBodyOfTrack,
} from '../../../services/interactScreenHelperService';
import {
	setQueueOperation,
	resetQueueOperation,
	setLiveQueueSlideItem,
	updateInteractTimelineItem,
	createInteractTimelineGroup,
	createInteractTimelineItem,
	removeInteractTimelineItem,
	removeInteractTimelineGroup,
} from '../../../actions/interactTimelineActions';
import { showMessage as showMessageAction } from '../../../actions/globalActions';
import { getMediaQueryDetails } from '../../../actions/publishActions';
import { deleteTrackFromMedia } from '../../../actions/inspectScreenActions';
import { createSequence, createTrack, updateSequence } from '../../../actions/tracksActions';
import { updateTimelineItem } from '../../../actions/liveManagerActions';
import { liveEventStatusList } from '../../../constants/liveEventStatus';
import { mediaNames, messageTypes } from '../../../constants/mediaConstants';
import { interactTimelineIcons, sequenceTypes, tracksSequencesTypes } from '../../../constants/interactConstant';
import { streamSetupType } from '../../../constants/liveManagerConstants';
import { getSecondsFromTimelineTime, getTimelineStartDate } from '../../../services/timeStampService';

const SLIDE_GROUP_ID = 'pdf-slide';
const SLIDE_GROUP_ORDER = 3;

interface LiveManagerActionsProps {
	mediaId: string;
	setMediaDetails: (_data: any) => void;
	closeOpenSequencesRef?: React.MutableRefObject<((_endSeconds: number) => void) | null>;
};

interface InteractPayload {
	id: string;
	resourceId: string;
	index: number;
};

const useLiveManagerActions = ({ mediaId, setMediaDetails, closeOpenSequencesRef }: LiveManagerActionsProps) => {
	const { t } = useTranslation();
	const dispatch = useDispatch() as any;
	const defaultAccountId = useSelector((state: any) => state.session.defaultAccountId);
	const store = useStore();

	const [ trackId, setTrackId ] = useState<string>('');

	const showMessage = (stackMessage: string, type: string) => {
		dispatch(showMessageAction(stackMessage, type));
	};

	const addToLiveQueue = (item: any) => {
		const state = store.getState();
		const { liveEventStatus, liveStreamPositionTime: currentPosition, streamSetup } = state.liveManager;
		const { queueOperationInProgress } = state.interactTimelineReducer;

		const liveStreamPositionTime = streamSetup === streamSetupType.MEETING
			? new Date(currentPosition.getTime() + 10000)
			: currentPosition;

		if (liveEventStatus !== liveEventStatusList.STARTED) {
			showMessage(
				t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_START_TO_ADD_QUEUE'),
				messageTypes.error
			);
			return;
		}
		if (!queueOperationInProgress) {
			dispatch(setQueueOperation());
			showMessage(
				t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_MSG_ADD_TO_QUEUE'),
				messageTypes.info
			);
			// Closing is left to the ledger in LiveManagerPresenter, which knows every slide that is
			// on air whoever opened it, and closes them at this same position. Closing
			// `liveQueueSlideItem` here as well wrote a second, later end over the correct one every
			// time the speaker panel had moved the slides on in between.
			closeOpenSequencesRef?.current?.(getSecondsFromTimelineTime(liveStreamPositionTime));
			modifyTimeOfQueueElement(
				item,
				liveStreamPositionTime,
				null,
				(error: string) => {
					dispatch(resetQueueOperation());
					if (error) {
						showMessage(
							t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_FAILED_TO_ADD_TO_LIVE_QUEUE'),
							messageTypes.error
						);
						return;
					}
					showMessage(
						t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_ADD_TO_LIVE_QUEUE'),
						messageTypes.info
					);
					dispatch(setLiveQueueSlideItem(item));
				}
			); //set start and end time
		} else {
			showMessage(
				t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_QUEUE_OPERATION_IN_PROGRESS'),
				messageTypes.error
			);
		}
	};

	const removePreset = (item: any) => {
		dispatch(setQueueOperation());
		removeFromLiveQueue(item, () => {
			dispatch(setLiveQueueSlideItem(''));
			dispatch(resetQueueOperation());
		});
	};

	const removeFromLiveQueue = (item: any, callback: any) => {
		const state = store.getState();
		const { liveStreamPositionTime: currentPosition, streamSetup } = state.liveManager;

		const liveStreamPositionTime = streamSetup === streamSetupType.MEETING
			? new Date(currentPosition.getTime() + 10000)
			: currentPosition;

		showMessage(
			t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_MSG_REMOVE_FROM_QUEUE'),
			messageTypes.info
		);

		if (item) {
			modifyTimeOfQueueElement(
				item,
				item.start,
				liveStreamPositionTime,
				(error: string) => {
					if (error) {
						showMessage(
							t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_FAILED_TO_REMOVE_FROM_LIVE_QUEUE'),
							messageTypes.error
						);
						dispatch(resetQueueOperation());
						return;
					}
					if (callback) {
						callback();
					}
				}
			);
		}
	};

	const modifyTimeOfQueueElement = (item: any, startTime: string, endTime: string | null, callback: (_error: string) => void) => {
		const state = store.getState();
		const { liveStreamPositionTime: currentPosition, streamSetup } = state.liveManager;

		const liveStreamPositionTime = streamSetup === streamSetupType.MEETING
			? new Date(currentPosition.getTime() + 10000)
			: currentPosition;

		if (trackId) {
			item.start = startTime ? new Date(startTime) : null;
			item.end = endTime ? new Date(endTime) : null;
			if (item.start > liveStreamPositionTime) {
				showMessage(
					t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_TIME_GREATER_THAN_LIVE_STREAM'),
					messageTypes.error
				);
				dispatch(resetQueueOperation());
				return;
			}
			const body = getBodyOfSlideSequence(item);
			if (!body) {
				dispatch(resetQueueOperation());
				showMessage(
					t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_FAILED_TO_SAVE_INVALID_VALUES'),
					messageTypes.error
				);
				return;
			}
			dispatch(updateSequence(
				defaultAccountId,
				mediaId,
				trackId,
				tracksSequencesTypes.slides,
				item.id,
				body
			)).then((res: any) => {
				if (typeof res === 'string') {
					dispatch(updateInteractTimelineItem(item, 'setScreenUnModified'));
					dispatch(updateTimelineItem(item));
					callback('');
				} else {
					callback('error');
				}
			});
		}
	};

	const findAndSetExistingTrack = (tracks: any[]) => {
		const track = tracks.find((i: any) => i.sequences && i.sequences.length && i.sequences[0].type === sequenceTypes.slide);
		if (track) {
			setTrackId(track.id);
		}
		return track;
	};

	const addInteractItemToTracks = (_item: InteractPayload) => {
		const state = store.getState();
		const { groups: interactGroups } = state.interactTimelineReducer;

		// create the slide interact group if doesn't exist already
		const interactGroup = interactGroups.find((i: any) => i.id === SLIDE_GROUP_ID);
		if (!interactGroup) {
			const group = {
				id: SLIDE_GROUP_ID,
				className: SLIDE_GROUP_ID,
				content: `<img src=${interactTimelineIcons.slidesIcon} />`,
				order: SLIDE_GROUP_ORDER,
				groupEditable: false,
			};
			dispatch(createInteractTimelineGroup(group));
		};

		const item = {
			id: _item.id,
			start: null,
			end: null,
			itemType: sequenceTypes.slide,
			resource: {
				id: _item.resourceId,
			},
			className: 'slideItem',
			name: `Slide ${_item.index}`,
			content: `Slide ${_item.index}`,
			editable: false,
			group: SLIDE_GROUP_ID,
			target: 'item',
			templateGroup: SLIDE_GROUP_ID,
		};

		dispatch(getMediaQueryDetails(defaultAccountId, mediaId, mediaNames.medias)).then((media: any) => {
			if (!media || typeof media == 'string') {
				return;
			}
			let tracks = media.tracks || [];
			let existingTrack = findAndSetExistingTrack(tracks);
			if (existingTrack) {
				// don't create a new track and just create a new sequence
				createSequenceOnly(item, existingTrack.id);
			} else {
				// create a new track with a new sequence if no track exists on the server for sequences with type slide
				createTracksWithSequence(item);
			}
		});
	};

	const createSequenceOnly = (item: any, currentTrackId: string) => {
		let body = getBodyOfSlideSequence(item);
		if (!body) {
			dispatch(resetQueueOperation());
			showMessage(
				t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_FAILED_TO_SAVE_INVALID_VALUES'),
				messageTypes.error
			);
			return;
		}
		dispatch(createSequence(defaultAccountId, mediaId, currentTrackId, tracksSequencesTypes.slides, body))
			.then((res: any) => {
				if (typeof res === 'string') {
					showMessage(
						t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_INTERACTIVE_MANAGER_CREATED_INTERACT_ITEMS'),
						messageTypes.info
					);
					dispatch(createInteractTimelineItem(item, 'setScreenUnModified'));

					// refresh the media with the new sequence
					dispatch(getMediaQueryDetails(defaultAccountId, mediaId, mediaNames.medias)).then((media: any) => {
						if (!media || typeof media == 'string') {
							return;
						}
						setMediaDetails(media);
					});
				} else {
					showMessage(
						t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_FAILED_TO_SAVE') + ' ' + (item.name),
						messageTypes.error
					);
				}
			});
	};

	const createTracksWithSequence = (item: any) => {
		let trackBody = getBodyOfTrack('pdf-track');
		showMessage(t('COMMON_WAIT_TO_SAVE_CHANGE'), messageTypes.info);
		dispatch(createTrack(defaultAccountId, mediaId, trackBody)).then((res: any) => {
			if (typeof res === 'string') {
				setTrackId(trackBody.id);
				createSequenceOnly(item, trackBody.id);
			}
		});
	};

	// Removes the presentation from the timeline: the whole pdf-track goes, which takes its slide
	// sequences with it, and the local timeline mirror is dropped along with them.
	//
	// The track must not be left behind empty. Both this screen and the viewer players look up the
	// FIRST track named 'pdf-track' (and the player treats the mere existence of one as "this media
	// has a presentation"), while createTrack mints a fresh id every time — so an empty leftover
	// would shadow the track the next upload creates and the presentation would render blank.
	const removeSlideTrack = async (): Promise<boolean | 'live'> => {
		const media = await dispatch(getMediaQueryDetails(defaultAccountId, mediaId, mediaNames.medias));
		// An unreadable media has to count as a failure: reporting success would let the caller
		// delete the slide resources while their sequences are still on the timeline. A media that
		// simply has no tracks left is a success — there is nothing to clean up.
		if (!media || typeof media === 'string') {
			return false;
		}
		// The server is the only source that sees every window: a moderator in another tab, or the
		// admin in this one, may have started the event between the click and this point. Stripping
		// the slides off a running event is the one outcome that must never happen.
		if (media.custom?.livemanager?.start) {
			return 'live';
		}
		const slideTracks = (media.tracks ?? []).filter((track: any) =>
			track.name === tracksSequencesTypes.pdfTrack ||
			track.sequences?.some((i: any) => i.type === sequenceTypes.slide)
		);

		const results = await Promise.all(slideTracks.map((track: any) =>
			dispatch(deleteTrackFromMedia(defaultAccountId, mediaId, track.id))
		));
		// a successful delete resolves to the (empty) response body, a failed one to undefined
		if (results.some((res: any) => typeof res !== 'string')) {
			return false;
		}

		slideTracks.forEach((track: any) => (track.sequences ?? []).forEach((sequence: any) => {
			dispatch(removeInteractTimelineItem(
				{ id: sequence.id, itemType: sequence.type, group: SLIDE_GROUP_ID },
				'setScreenUnModified'
			));
		}));
		dispatch(removeInteractTimelineGroup({ id: SLIDE_GROUP_ID }));
		dispatch(setLiveQueueSlideItem(''));
		// the id is gone; the next upload creates a new track and stores its id here
		setTrackId('');
		return true;
	};

	const addToLiveQueueAtStart = (item: any) => {
		modifyTimeOfQueueElement(
			item,
			getTimelineStartDate() as any,
			null,
			(error: string) => {
				if (error) {
					showMessage(
						t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_FAILED_TO_ADD_TO_LIVE_QUEUE'),
						messageTypes.error
					);
					return;
				}
				dispatch(setLiveQueueSlideItem(item));
			}
		);
	};

	// Closes a sequence outright, given the times to write. Deliberately not routed through
	// `modifyTimeOfQueueElement`: its checks are for scheduling a slide, and re-validating the
	// start against this manager's own player position aborts closes that are perfectly valid -
	// on a meeting stream the two tools stamp from different clocks and this player lags the edge.
	const closeSequenceAt = (sequenceId: string, resourceId: string, startSeconds: number, endSeconds: number) => {
		if (!trackId || !sequenceId || !resourceId || endSeconds <= startSeconds) {
			return;
		}

		dispatch(updateSequence(
			defaultAccountId,
			mediaId,
			trackId,
			tracksSequencesTypes.slides,
			sequenceId,
			{
				id: sequenceId,
				type: tracksSequencesTypes.slides,
				start: startSeconds,
				end: endSeconds,
				resource: { id: resourceId },
			}
		));
	};

	return {
		addInteractItemToTracks,
		removePreset,
		removeSlideTrack,
		addToLiveQueue,
		addToLiveQueueAtStart,
		removeFromLiveQueue,
		findAndSetExistingTrack,
		closeSequenceAt,
	};
};

export default useLiveManagerActions;
